import { queryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { fetchStudentConversations, fetchConversationMessages, postConversationMessage, postConversation, fetchStudentConversationCourseList, fetchStudentConversationCreateTypeList, fetchStudentInterventionSummary, updateStudentInterventionSummary } from './api'
import { Conversation, ConversationMessage, CreateConversationForStudentRequestFlat, CreateMessageForConversationRequestFlat, GetConversationsForStudentRequestFlat, GetConversationTypesForStudentRequestFlat, GetMessagesForConversationRequestFlat, GetStudentInterventionSummaryRequestFlat, StudentInterventionSummary, UpdateStudentInterventionSummaryRequestFlat } from '@godalming/api-types'
import { meSessionQueryOptions } from '@features/me/queries'

export const studentConversationsQueryOptions = (params: GetConversationsForStudentRequestFlat) =>
    queryOptions({
        queryKey: ['students', params.studentNo, 'conversations'],
        queryFn: () => fetchStudentConversations(params),
        staleTime: 600000
    })

export function useStudentConversationsQuery(params: GetConversationsForStudentRequestFlat) {
    return useQuery(studentConversationsQueryOptions(params))
}

export function useStudentConversationsSuspense(params: GetConversationsForStudentRequestFlat) {
    return useSuspenseQuery(studentConversationsQueryOptions(params))
}


export const conversationMessagesQueryOptions = (params: GetMessagesForConversationRequestFlat) =>
    queryOptions({
        queryKey: ['conversations', params.conversationId, 'messages'],
        queryFn: () => fetchConversationMessages(params),
        select: (msgs) =>
        [...msgs].sort((a, b) => {
            const ta = new Date(a.dateEntered).getTime();
            const tb = new Date(b.dateEntered).getTime();
            return ta - tb;
        }),
    })

export function useConversationMessagesQuery(params: GetMessagesForConversationRequestFlat) {
    return useQuery(conversationMessagesQueryOptions(params))
}

export function useConversationMessagesSuspense(params: GetMessagesForConversationRequestFlat) {
    return useSuspenseQuery(conversationMessagesQueryOptions(params))
}


export const conversationTypesQueryOptions = (params: GetConversationTypesForStudentRequestFlat) =>
    queryOptions({
        queryKey: ['students', params.studentNo, 'conversationTypes'],
        queryFn: () => fetchStudentConversationCreateTypeList(params),
        staleTime: Infinity
    })

export function useConversationTypesQuery(params: GetConversationTypesForStudentRequestFlat) {
    return useQuery(conversationTypesQueryOptions(params))
}

export function useConversationTypesSuspense(params: GetConversationTypesForStudentRequestFlat) {
    return useSuspenseQuery(conversationTypesQueryOptions(params))
}


export const conversationCoursesQueryOptions = (params: GetConversationTypesForStudentRequestFlat) =>
    queryOptions({
        queryKey: ['students', params.studentNo, 'conversationCourses'],
        queryFn: () => fetchStudentConversationCourseList(params),
        staleTime: Infinity
    })

export function useConversationCoursesQuery(params: GetConversationTypesForStudentRequestFlat) {
    return useQuery(conversationCoursesQueryOptions(params))
}

export function useConversationCoursesSuspense(params: GetConversationTypesForStudentRequestFlat) {
    return useSuspenseQuery(conversationCoursesQueryOptions(params))
}



export const studentInterventionSummaryQueryOptions = (params: GetStudentInterventionSummaryRequestFlat) =>
    queryOptions<StudentInterventionSummary>({
        queryKey: ['students', params.studentNo, 'interventionSummary'],
        queryFn: () => fetchStudentInterventionSummary(params),
    })

export function useStudentInterventionSummaryQuery(params: GetStudentInterventionSummaryRequestFlat) {
    return useQuery(studentInterventionSummaryQueryOptions(params))
}

export function useStudentInterventionSummarySuspense(params: GetStudentInterventionSummaryRequestFlat) {
    return useSuspenseQuery(studentInterventionSummaryQueryOptions(params))
}

export function useUpdateStudentInterventionSummaryMutation() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (vars: UpdateStudentInterventionSummaryRequestFlat) => updateStudentInterventionSummary(vars),
        onSettled: (_data, _error, variables) => {
            queryClient.invalidateQueries({
                queryKey: ['students', variables.studentNo, 'interventionSummary']
            })
        }
    })
}
export function useCreateMessageMutation() {
    const queryClient = useQueryClient()
    const me = queryClient.getQueryData(meSessionQueryOptions().queryKey)?.actualStaffProfile

    return useMutation({
        // Called when you do mutate(variables)
        mutationFn: async (vars: CreateMessageForConversationRequestFlat & { studentNo: string }) => postConversationMessage(vars),
        onMutate: async (vars) => {
            const conversationId = Number(vars.conversationId)
            const now = new Date().toISOString()

            // Stop in-flight fetches from overwriting your optimistic write
            await queryClient.cancelQueries(conversationMessagesQueryOptions({conversationId: vars.conversationId.toString()}))

            // Snapshot for rollback
            const prevMessages = queryClient.getQueryData<ConversationMessage[]>(conversationMessagesQueryOptions({conversationId: vars.conversationId.toString()}).queryKey)
            const prevConversations = queryClient.getQueryData<Conversation[]>(studentConversationsQueryOptions({studentNo: vars.studentNo.toString()}).queryKey)
            const optimisticMessage: ConversationMessage = {
                dateEntered: now,
                idStaff: Number(me?.staffId ?? -1),
                message: vars.message,
                staffName: `${me?.firstName} ${me?.surname}`,
                idConversation: conversationId,
                idConversationMessage: -1, // Temporary ID
                createdBy: `${me?.initials}`,
            }

            queryClient.setQueryData<ConversationMessage[]>(
                conversationMessagesQueryOptions({conversationId: vars.conversationId.toString()}).queryKey,
                (old = []) => [...old, optimisticMessage], // or [optimisticMessage, ...old] if newest-first
            )

            queryClient.setQueryData<Conversation[]>(
                studentConversationsQueryOptions({studentNo: vars.studentNo}).queryKey,
                (old) =>
                old?.map((c) =>
                    c.idConversation === conversationId
                    ? { ...c, dateLastEntered: now }
                    : c,
                ) ?? old,
            )

            return { conversationId, studentNo: vars.studentNo, prevMessages, prevConversations }
        },

        onError: (err, _vars, context) => {
            console.error('Error posting message, rolling back optimistic update:', err)
            if (!context) return

            queryClient.setQueryData(conversationMessagesQueryOptions({conversationId: context.conversationId.toString()}).queryKey, context.prevMessages)
            queryClient.setQueryData(studentConversationsQueryOptions({studentNo: context.studentNo.toString()}).queryKey, context.prevConversations)
        },

        onSettled: (_data, _err, vars) => {
            // Always reconcile with server (covers success + error cases)
            queryClient.invalidateQueries({ queryKey: conversationMessagesQueryOptions({conversationId: vars.conversationId.toString()}).queryKey })
            queryClient.invalidateQueries({ queryKey: studentConversationsQueryOptions({studentNo: vars.studentNo.toString()}).queryKey })
        },
    })
}

export function useCreateConversationMutation() {
    const queryClient = useQueryClient()

    return useMutation({
        // Called when you do mutate(variables)
        mutationFn: async (vars: CreateConversationForStudentRequestFlat) => postConversation(vars),

        onMutate(variables, context) {
            const now = new Date().toISOString()

            // Stop in-flight fetches from overwriting your optimistic write
            queryClient.cancelQueries(studentConversationsQueryOptions({studentNo: variables.studentNo.toString()}))

            // Snapshot for rollback
            const prevConversations = queryClient.getQueryData<Conversation[]>(studentConversationsQueryOptions({studentNo: variables.studentNo.toString()}).queryKey)

            const optimisticConversation: Conversation = {
                dateCreated: now,
                dateLastEntered: now,
                idConversation: -1, // Temporary ID; real ID comes from server
                nameTypeCfC: 'Loading...',
                descSubject: null,
                isConversationClosed: false,
                createdBy: queryClient.getQueryData(meSessionQueryOptions().queryKey)?.actualStaffProfile?.initials ?? 'NA',
            }

            queryClient.setQueryData<Conversation[]>(
                studentConversationsQueryOptions({studentNo: variables.studentNo.toString()}).queryKey,
                (old = []) => [optimisticConversation, ...old],
            )

            return { studentNo: variables.studentNo, prevConversations }
        },

        onError: (err, _vars, context) => {
            if (!context) return
            queryClient.setQueryData(studentConversationsQueryOptions({studentNo: context.studentNo.toString()}).queryKey, context.prevConversations)
        },

        // Update caches when the mutation succeeds
        onSettled(data, error, variables, onMutateResult, context) {
            queryClient.invalidateQueries({queryKey: ['students', variables.studentNo.toString(), 'conversations']})
        }
    })
}
